import { useState } from "react";
import { api } from "../api";

/**
 * A link-looking control that opens or saves a file the backend guards.
 *
 * It is a `<button>`, not an `<a href>`, and that is the whole point rather
 * than a styling preference. An anchor navigates, a navigation carries no
 * Authorization header, and the endpoint answers `{"detail":"Not
 * authenticated"}` — which the browser then renders where the document should
 * be. See the "Authenticated files" section in api.js.
 *
 * `mode="open"` shows the file in a new tab — the portal's viewer page,
 * `/view`, with Back to portal and Download (PDFs, which a browser renders);
 * `mode="save"` writes it to disk (formats it cannot).
 */
export default function FileLink({ path, mode = "open", children = "PDF", style }) {
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  async function go() {
    setError(null);
    setBusy(true);
    try {
      await (mode === "save" ? api.saveFile(path) : api.openFile(path));
    } catch (err) {
      // An expired session is already being handled by api.js — it signs the
      // user out — so saying it again here would flash a message on a page
      // that is on its way to /login.
      if (err.name !== "AuthExpiredError") setError(err.message || "Could not fetch the file");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button type="button" onClick={go} disabled={busy}
              style={{ background: "none", border: "none", padding: 0,
                       cursor: busy ? "default" : "pointer", fontFamily: "inherit",
                       fontSize: 12, color: "#374151", textDecoration: "underline",
                       ...style }}>
        {busy ? "…" : children}
      </button>
      {error && (
        <span role="alert" style={{ fontSize: 12, color: "#b91c1c", marginLeft: 8 }}>
          {error}
        </span>
      )}
    </>
  );
}
